import { isNativeApp } from '../lib/platform';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useGroup } from '../context/GroupContext';
import { useUser } from '../context/AuthContext';
import useOpenSettlements from '../hooks/useOpenSettlements';
import useMatches from '../hooks/useMatches';

const SETTLEMENT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const MATCH_REMINDER_WINDOW_MS = 4 * 60 * 60 * 1000;

const isQuietHours = (date) => {
  const hour = date.getHours();
  return hour >= 22 || hour < 9;
};

const AutomatedNotificationManager = () => {
  const { user } = useUser();
  const { group } = useGroup();
  const { openCount } = useOpenSettlements();
  const { matches = [] } = useMatches();
  const runningRef = useRef(false);

  const upcomingMatch = useMemo(() => {
    if (!matches.length) return null;
    const now = new Date();
    return matches.find((m) => new Date(m.date) > now) || null;
  }, [matches]);

  const getStorageKey = useCallback((suffix) => {
    const uid = user?.id || 'anon';
    const gid = group?.id || 'nogroup';
    return `bb:auto-notify:${gid}:${uid}:${suffix}`;
  }, [group?.id, user?.id]);

  const ensureNotificationReady = useCallback(async () => {
    if (isNativeApp() || !('Notification' in window)) return null;
    if (!('serviceWorker' in navigator)) return null;
    if (Notification.permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.ready;
    if (!registration?.active) return null;
    return registration;
  }, []);

  const evaluateAndNotify = useCallback(async () => {
    if (!user?.id || !group?.id) return;
    if (runningRef.current) return;
    runningRef.current = true;

    try {
      const now = new Date();
      if (isQuietHours(now)) return;

      const registration = await ensureNotificationReady();
      if (!registration) return;

      // 1) Settlement reminder (max 1x per 24h)
      if (openCount > 0) {
        const settlementLastSentKey = getStorageKey('settlement:last_sent_at');
        const lastSent = Number(localStorage.getItem(settlementLastSentKey) || 0);

        if (!lastSent || Date.now() - lastSent >= SETTLEMENT_COOLDOWN_MS) {
          const body = openCount === 1
            ? 'Je hebt nog 1 openstaande verrekening. Fix je ’m voor de aftrap?'
            : `Je hebt nog ${openCount} openstaande verrekeningen. Tijd om ze af te ronden?`;

          registration.active.postMessage({
            type: 'SETTLEMENT_DUE_NOTIFICATION',
            payload: {
              title: 'Openstaande verrekening 💸',
              body,
              url: '/settlements',
            },
          });

          localStorage.setItem(settlementLastSentKey, String(Date.now()));
        }
      }

      // 2) Match reminder (1x per specifieke upcoming match)
      if (upcomingMatch?.id && upcomingMatch?.date) {
        const matchDateMs = new Date(upcomingMatch.date).getTime();
        const delta = matchDateMs - Date.now();

        if (delta > 0 && delta <= MATCH_REMINDER_WINDOW_MS) {
          const matchReminderKey = getStorageKey(`match:${upcomingMatch.id}:sent`);
          const alreadySent = localStorage.getItem(matchReminderKey) === '1';

          if (!alreadySent) {
            const previousMatchBeerCount = Number(localStorage.getItem('bb:last-match-beers-total') || 0);
            const fact = previousMatchBeerCount > 0
              ? `Vorige wedstrijd werden er ${previousMatchBeerCount} biertjes gedronken.`
              : 'Vorige keer ging het stevig los 🍺.';

            registration.active.postMessage({
              type: 'MATCH_REMINDER_NOTIFICATION',
              payload: {
                title: 'Klaar voor vanmiddag? ⚽🍻',
                body: `${fact} Vandaag weer tegen ${upcomingMatch.opponent || 'de volgende tegenstander'}.`,
                url: '/input',
              },
            });

            localStorage.setItem(matchReminderKey, '1');
          }
        }
      }
    } finally {
      runningRef.current = false;
    }
  }, [ensureNotificationReady, getStorageKey, group?.id, openCount, upcomingMatch?.date, upcomingMatch?.id, upcomingMatch?.opponent, user?.id]);

  useEffect(() => {
    evaluateAndNotify();
    const timer = window.setInterval(evaluateAndNotify, CHECK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [evaluateAndNotify]);

  return null;
};

export default AutomatedNotificationManager;
